<template>
  <v-combobox
      :value="selection"
      :items="vehicles"
      item-text="registrationNumber"
      item-value="id"
      :placeholder="placeholder"
      :disabled="disabled"
      :hint="hint"
      :persistent-hint="!!hint"
      :filter="filterFn"
      return-object
      clearable
      hide-no-data
      :search-input.sync="searchInput"
      @input="onInput"
      @blur="onBlur"
  >
    <template #item="{item}">
      <v-list-item-content>
        <v-list-item-title>{{ item.registrationNumber }}</v-list-item-title>
        <v-list-item-subtitle v-if="vehicleSubtitle(item)">
          {{ vehicleSubtitle(item) }}
        </v-list-item-subtitle>
      </v-list-item-content>
    </template>
    <template #selection="{item}">
      <span>{{ item && typeof item === 'object' ? item.registrationNumber : item }}</span>
    </template>
  </v-combobox>
</template>

<script>
function normalizePlate(value) {
  if (!value) return ''
  return String(value).toUpperCase().replace(/[^A-Z0-9]/g, '')
}

export default {
  name: 'VehicleDefaultSelector',

  model: {
    prop: 'value',
    event: 'change',
  },

  props: {
    // Payload emitted upward: { defaultVehicleId?, defaultVehicleRegistrationNumber?, clearDefaultVehicle? }
    value: { type: Object, default: () => ({}) },
    vehicles: { type: Array, default: () => [] },
    initialVehicleId: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: '' },
  },

  data() {
    return {
      // Mirror of the combobox's typed text. Needed because v-combobox only
      // emits free text via @input on blur/Enter — if the user types a plate
      // and clicks "Save" without leaving the field, @input may not fire and
      // the plate is lost. @blur reprocesses this value to guarantee emission.
      searchInput: null,
    }
  },

  computed: {
    selection() {
      const v = this.value || {}
      if (v.clearDefaultVehicle) return null
      if (v.defaultVehicleId) {
        return this.vehicles.find(x => x.id === v.defaultVehicleId) || null
      }
      if (typeof v.defaultVehicleRegistrationNumber === 'string') {
        return v.defaultVehicleRegistrationNumber
      }
      if (this.initialVehicleId) {
        return this.vehicles.find(x => x.id === this.initialVehicleId) || null
      }
      return null
    },

    hint() {
      if (this.disabled) return this.$t('member.vehicle-selector.disabled-hint')
      const v = this.value || {}
      if (typeof v.defaultVehicleRegistrationNumber === 'string'
          && v.defaultVehicleRegistrationNumber.trim()) {
        const norm = normalizePlate(v.defaultVehicleRegistrationNumber)
        const match = this.vehicles.find(x => normalizePlate(x.registrationNumber) === norm)
        if (!match) {
          return this.$t('member.vehicle-selector.will-create-hint')
        }
      }
      return ''
    },
  },

  methods: {
    vehicleSubtitle(item) {
      if (!item || typeof item !== 'object') return ''
      return [item.brand, item.model].filter(Boolean).join(' ')
    },

    filterFn(item, queryText) {
      const q = normalizePlate(queryText)
      if (!q) return true
      return normalizePlate(item.registrationNumber).includes(q)
    },

    onBlur() {
      const text = (this.searchInput || '').trim()
      if (!text) return

      // Skip if the typed text already matches the committed selection
      // (avoids a redundant emit when nothing actually changed).
      const sel = this.selection
      const selText = sel && typeof sel === 'object' ? sel.registrationNumber : sel
      if (selText && normalizePlate(selText) === normalizePlate(text)) return

      // Force-process the uncommitted free text (idempotent with @input).
      this.onInput(text)
    },

    onInput(val) {
      if (val == null) {
        const hadDefault = this.initialVehicleId
            || (this.value && this.value.defaultVehicleId)
            || (this.value && typeof this.value.defaultVehicleRegistrationNumber === 'string')
        this.$emit('change', hadDefault ? { clearDefaultVehicle: true } : {})
        return
      }
      if (typeof val === 'object' && val.id) {
        this.$emit('change', val.id === this.initialVehicleId ? {} : { defaultVehicleId: val.id })
        return
      }
      if (typeof val === 'string') {
        const trimmed = val.trim()
        if (!trimmed) {
          this.$emit('change', this.initialVehicleId ? { clearDefaultVehicle: true } : {})
          return
        }
        const norm = normalizePlate(trimmed)
        const match = this.vehicles.find(x => normalizePlate(x.registrationNumber) === norm)
        if (match) {
          this.$emit('change', match.id === this.initialVehicleId ? {} : { defaultVehicleId: match.id })
        } else {
          this.$emit('change', { defaultVehicleRegistrationNumber: trimmed })
        }
      }
    },
  },
}
</script>
